import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { CalendarIcon } from "lucide-react";
import { getBase64 } from "../helpers/commonHelper";
import ChangePassword from "../components/ChangePassword";
import { useDispatch } from "react-redux";
import { getMyProfile, updateProfile } from "../actions/profileAction";
import { useMediaQuery } from "@/hooks/use-media-query";
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import useSEO from "../hooks/useSEO";

const formSchema = z.object({
  FirstName: z.any(),
  LastName: z.any(),
  Gender: z.any(),
  DateOfBirth: z.any(),
  PhoneNumber: z.any(),
  Email: z.any(),
  Base64ImgFile: z.any(),
});

function MyProfile() {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      FirstName: "",
      LastName: "",
      Gender: "",
      DateOfBirth: "",
      PhoneNumber: "",
      Email: "",
      Base64ImgFile: "",
    },
  });
  const { reset } = form;
  const dispatch = useDispatch();
  const [imageFile, setImageFile] = useState('');
  const [isCalenderOpen, setIsCalenderOpen] = useState(false);

  const { setSEO } = useSEO();
  useEffect(() => {
    setSEO('My Profile | Yatra Tickets', 'Manage your profile information');
  }, [setSEO]);

  const onSubmit = (data) => {
    // Do something with the form values.
    // ✅ This will be type-safe and validated.
    dispatch(updateProfile(data, () => { }));
  };
  const handleImageChange = (e) => {
    if (e.target.files.length > 0) {
      var file = e.target.files[0];
      getBase64(file).then((data) => {
        setImageFile(data);
        form.setValue("Base64ImgFile", data);
      });
    }
  };
  useEffect(() => {
    dispatch(
      getMyProfile((info) => {
        reset({
          FirstName: info.FirstName,
          LastName: info.LastName,
          Gender: info.Gender.toString(),
          DateOfBirth: info.DateOfBirth,
          Email: info.Email,
          PhoneNumber: info.PhoneNumber ?? ""
        })
        setImageFile(info.ProfileImage);
      })
    );
  }, []);
  return (
    <div className="bg-background">
      <div className="container m-auto p-4 md:py-6">
        <h1 className="mb-4 text-xl font-bold">My Profile</h1>
        <div className="border-b py-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="mb-4">
              <h2 className="text-2xl font-semibold leading-none tracking-tight mb-2">
                Personal Information
              </h2>
              <p className="text-sm text-muted-foreground">
                Use a permanent address where you can receive mail.
              </p>
            </div>
            <Form {...form}>
              <div className="col-span-2">
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  <div className="mb-6">
                    <div className="flex gap-2 align-center">
                      <Avatar className="w-20 h-20 me-4">
                        <AvatarImage src={imageFile} alt="" />
                        <AvatarFallback>CN</AvatarFallback>
                      </Avatar>

                      <div className="flex-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="mb-2 cursor-default"
                          type="button"
                        >
                          <label
                            htmlFor="imageInput"
                            className="cursor-pointer"
                          >
                            Change avatar
                          </label>
                        </Button>
                        <Input
                          type="file"
                          id="imageInput"
                          className="hidden"
                          accept=".jpg,.gif,.png"
                          onChange={handleImageChange}
                        />
                        <p className="text-sm text-muted-foreground">
                          JPG, GIF or PNG. 1MB max.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="mb-4">
                      <FormField
                        control={form.control}
                        name="FirstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>First Name </FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormDescription />
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="mb-4">
                      <FormField
                        control={form.control}
                        name="LastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Last Name </FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormDescription />
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="mb-4">
                      <FormField
                        control={form.control}
                        name="Gender"
                        render={({ field }) => (
                          <FormItem className="space-y-3">
                            <FormLabel>Gender</FormLabel>
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                value={field.value}
                                className="grid grid-cols-3 gap-4 mb-4"
                              >
                                <FormItem className="flex items-center space-x-3 space-y-0">
                                  <RadioGroupItem value="1" />
                                  <FormLabel>Male</FormLabel>
                                </FormItem>
                                <FormItem className="flex items-center space-x-3 space-y-0">
                                  <RadioGroupItem value="2" />
                                  <FormLabel>Female</FormLabel>
                                </FormItem>
                                <FormItem className="flex items-center space-x-3 space-y-0">
                                  <RadioGroupItem value="3" />
                                  <FormLabel>Other</FormLabel>
                                </FormItem>
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="mb-4">
                      <FormField
                        control={form.control}
                        name="DateOfBirth"
                        render={({ field }) => (
                          <FormItem className='space-y-2'>
                            {isDesktop ?
                              <Popover open={isCalenderOpen} onOpenChange={setIsCalenderOpen}>
                                <PopoverTrigger asChild>
                                  <FormControl>
                                    <Button
                                      variant={"outline"}
                                      className={cn(
                                        " pl-3 text-left font-normal w-full",
                                        !field.value && "text-muted-foreground"
                                      )}
                                    >
                                      {field.value ? (
                                        format(field.value, "PPP")
                                      ) : (
                                        <span>Pick a date</span>
                                      )}
                                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                    </Button>
                                  </FormControl>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                  <Calendar
                                    mode="single"
                                    captionLayout="dropdown-buttons"
                                    fromYear={1900}
                                    toYear={new Date().getFullYear}
                                    selected={field.value}
                                    onSelect={(e) => { field.onChange(e); setIsCalenderOpen(false); }}
                                    disabled={(date) =>
                                      date > new Date() || date < new Date("1900-01-01")
                                    }
                                    initialFocus
                                  />
                                </PopoverContent>
                              </Popover>
                              :
                              <Drawer open={isCalenderOpen} onOpenChange={setIsCalenderOpen}>
                                <DrawerTrigger asChild>
                                  <FormControl>
                                    <Button
                                      variant={"outline"}
                                      className={cn(
                                        " pl-3 text-left font-normal",
                                        !field.value && "text-muted-foreground w-full"
                                      )}
                                    >
                                      {field.value ? (
                                        format(field.value, "PPP")
                                      ) : (
                                        <span>Pick a date</span>
                                      )}
                                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                    </Button>
                                  </FormControl>
                                </DrawerTrigger>
                                <DrawerContent>
                                  <Calendar
                                    className="flex-1"
                                    mode="single"
                                    captionLayout="dropdown-buttons"
                                    fromYear={1960}
                                    toYear={2030}
                                    selected={field.value}
                                    onSelect={(e) => {
                                      setIsCalenderOpen(false);
                                      field.onChange(e);
                                    }}
                                    disabled={(date) =>
                                      date > new Date() || date < new Date("1900-01-01")
                                    }
                                    initialFocus
                                  />
                                </DrawerContent>
                              </Drawer>}
                          </FormItem>
                        )} />

                      {/* <FormField
                        control={form.control}
                        name="DateOfBirth"
                        render={({ field }) => (
                          <FormItem className="flex flex-col">
                            <FormLabel>DOB</FormLabel>
                            <Popover open={isCalenderOpen} onOpenChange={setIsCalenderOpen}>
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <Button variant={"outline"} type="button">
                                    {field.value ? (
                                      format(field.value, "PPP")
                                    ) : (
                                      <span>Pick a date</span>
                                    )}
                                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                  </Button>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent
                                className="w-auto p-0"
                                align="start"
                              >
                                <Calendar
                                  hidecaptionlabel
                                  captionLayout="dropdown-buttons"
                                  fromYear={new Date().getFullYear() - 130}
                                  toYear={new Date().getFullYear()}
                                  selected={field.value}
                                  onSelect={field.onChange}
                                  disabled={(date) =>
                                    date > new Date() ||
                                    date < new Date("1900-01-01")
                                  }
                                  initialFocus

                                />
                              </PopoverContent>
                            </Popover>
                            <FormDescription>
                              Your date of birth is used to calculate your age.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      /> */}
                      {/* <Label className="mb-2 block" htmlFor="lastname">
                        DOB
                      </Label>
                      <Input type="lastname" /> */}
                    </div>
                    <div className="mb-4">
                      <FormField
                        control={form.control}
                        name="PhoneNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Contact Number</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormDescription />
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="mb-4">
                      <FormField
                        control={form.control}
                        name="Email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormDescription />
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                  <div className="flex">
                    <Button type="submit" size="sm">
                      Save Changes
                    </Button>
                  </div>
                </form>
              </div>
            </Form>
          </div>
        </div>
        <ChangePassword />
      </div>
    </div>
  );
}

export default MyProfile;
